/**
 * ehr#437 (PROGRESS_NOTE_10-14-V81): the note's Session details math (times, length, the billing
 * code) and what is still missing before it can be signed off. Pure, so it's tested on its own.
 */
import { PSYCH_TESTING_CODE_CHOICES, THERAPY_KINDS } from '@ehr/shared';
import { noteTypeOf } from './noteTypes.js';

/** ehr#726: the Psych testing kind, the one kind outside the Service notes with a default code. */
export const PSYCH_TESTING_KIND = 'Psych testing';

// ehr#663: the times and the sign-off checks live in @ehr/shared (the API checks co-signing too).
export {
  THERAPY_KINDS,
  addMinutes,
  GOAL_FINAL_KINDS,
  goalStatusRows,
  leftToSign,
  noteLeftToSign,
  sessionEndOf,
  sessionMinutes,
  type SignCheck,
} from '@ehr/shared';

/** The Length buttons. */
export const LENGTHS = [30, 45, 50, 60, 90] as const;

/** V81 NEC_PHRASES: one-click lines for Why treatment continues. */
export const NECESSITY_PHRASES = [
  'Symptoms still interfere with work, sleep or relationships.',
  'Without weekly sessions the client is at risk of losing the gains made so far.',
  'Progress is steady but the treatment plan goals are not yet met.',
  'Risk factors are present and need continued monitoring.',
] as const;

/** V81 SVC_CODE_DEFAULTS: the codes a Service note bills under. */
export const SERVICE_CODES: Record<string, string> = {
  'Case management': 'H0006',
  'Peer support': 'H0038',
  'Skills / rehab': 'H2014',
  'Collateral contact': '90846',
};
/** V81 UNIT_CODES: timed codes, billed in units of this many minutes. */
export const UNIT_CODES: Record<string, number> = {
  // ehr#478: the rest of V81's list, now that a code can be picked on the note
  T1016: 15,
  T1017: 15,
  H0038: 15,
  H2014: 15,
  H2017: 15,
  '96136': 30,
  '96137': 30,
  '96130': 60,
  '96131': 60,
};
/**
 * Units of a timed code (V81 unitsOf): 15-minute codes follow the CMS 8-minute rule (8 to 22 min
 * = 1, 23 to 37 = 2, …); longer units need more than half for the first.
 */
export function unitsOf(code: string, minutes: number | null): number | null {
  const u = UNIT_CODES[code];
  if (!u || !(minutes && minutes > 0)) return null;
  return Math.floor((minutes + Math.ceil(u / 2) - 1) / u);
}

/**
 * The billing code the note's kind and length make (V81 autoCode + codeText), e.g. "90834",
 * "90839 + 90840", "H0038 x2", or '' when it can't be told yet (no length) or is billed by hand.
 * CPT time ranges: 90832 16-37 min, 90834 38-52, 90837 53+; 90839 30+, one 90840 at 75, another
 * every 30 after. The kind is the note's own (ehr#437 part 3); else it comes from the type.
 */
export function billingCode(
  sessionType: string,
  minutes: number | null,
  noteKind?: string,
  // ehr#437 (CASE_MANAGEMENT_NOTES V58 21 B): the practice's Service note default codes; '' is no
  // default (billed by hand), not set is the built-in code.
  serviceCodes?: Partial<Record<string, string>>,
): string {
  const kind = noteKind || noteTypeOf({ sessionLabel: sessionType });
  // ehr#726 (PSYCH_TESTING_NOTE_10-30-V4, 10a): a Psych testing note starts with the practice's
  // default (96130 / 96136, in units from the length); none set (the start), picked by hand
  if (kind === PSYCH_TESTING_KIND) {
    const d = serviceCodes?.[kind] ?? '';
    if (!(PSYCH_TESTING_CODE_CHOICES as readonly string[]).includes(d)) return '';
    const n = unitsOf(d, minutes);
    return n && n > 1 ? `${d} x${n}` : d;
  }
  const set = serviceCodes?.[kind];
  const svc = set !== undefined && kind in SERVICE_CODES ? set : SERVICE_CODES[kind];
  if (set === '' && kind in SERVICE_CODES) return '';
  if (svc) {
    const n = unitsOf(svc, minutes);
    return n && n > 1 ? `${svc} x${n}` : svc;
  }
  if (kind === 'New Client') return '90791';
  if (kind === 'Family' || kind === 'Couples') return '90847';
  if (kind === 'Group') return '90853';
  if (!(THERAPY_KINDS as readonly string[]).includes(kind)) return ''; // billed by hand, or not at all
  if (minutes == null) return '';
  if (kind === 'Crisis') {
    if (minutes < 30) return '';
    const add = minutes >= 75 ? 1 + Math.floor((minutes - 75) / 30) : 0;
    return add ? `90839 + 90840${add > 1 ? ` x${add}` : ''}` : '90839';
  }
  if (minutes < 16) return '';
  return minutes <= 37 ? '90832' : minutes <= 52 ? '90834' : '90837';
}
